import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from '../config';
import sessionManager from '../utils/sessionManager';
import Logo from './Logo';
import './LoginOverlay.css';

const LoginOverlay = ({ initialMode = 'signin', onClose, returnUrl }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(initialMode); // Use initialMode prop
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [selectedRole, setSelectedRole] = useState('tenant'); // Default role for signup
  const [useForgotPassword, setUseForgotPassword] = useState(false); // For switching to OTP in signin

  const roles = [
    { key: 'tenant', icon: '🏠', title: 'Tenant', description: 'Looking for a place to rent' },
    { key: 'landlord', icon: '🏢', title: 'Landlord', description: 'I own rental properties' },
    { key: 'agent', icon: '👔', title: 'Real Estate Agent', description: 'I help clients find properties' },
    { key: 'property_manager', icon: '📋', title: 'Property Manager', description: 'I manage rental properties' },
  ];

  const navigate = useNavigate();

  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    if (!password) {
      setError('Please enter your password');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.AUTH_BASE_URL}${API_CONFIG.ENDPOINTS.SIGNIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store the auth token and user info
        sessionManager.setSession(
          data.authToken, 
          data.user?.role || 'tenant',
          data.user?.phone || phoneNumber
        );
        
        // Close overlay and navigate
        onClose();
        if (returnUrl) {
          navigate(returnUrl);
        } else {
          navigate('/');
        }
      } else {
        if (data.message && data.message.includes('Invalid password')) {
          setError('Invalid password. Please try again or use "Forgot Password".');
        } else if (data.message && data.message.includes('User not found')) {
          setError('Account not found. Please sign up or check your phone number.');
        } else {
          setError(data.message || 'Sign in failed');
        }
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Password sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.AUTH_BASE_URL}${API_CONFIG.ENDPOINTS.REQUEST_OTP}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          userRole: selectedRole, // Use selectedRole for signup
          isSignUp: mode === 'signup'
        })
      });

      const data = await response.json();

      if (response.ok) {
        sessionStorage.setItem('phoneNumber', phoneNumber);
        sessionStorage.setItem('pendingUserRole', selectedRole);
        setOtpSent(true);
      } else {
        if (data.message?.includes('not found') || data.message?.includes('not registered')) {
          setMode('signup'); // Automatically switch to signup if phone not found
          setError('Phone number not found. Creating new account...');
          // Retry as signup
          setTimeout(() => {
            handleRequestOtp(e);
          }, 1000);
        } else {
          setError(data.message || 'Failed to send verification code');
        }
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('OTP Submit - Debug Info:', {
      otp,
      phoneNumber,
      selectedRole,
      mode,
      useForgotPassword,
      isSignUp: mode === 'signup'
    });

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const requestBody = {
        phoneNumber: phoneNumber,
        otp: otp,
        userRole: selectedRole,
        isSignUp: mode === 'signup'
      };
      
      console.log('Sending OTP verification request:', requestBody);
      
      const response = await fetch(`${API_CONFIG.AUTH_BASE_URL}${API_CONFIG.ENDPOINTS.VERIFY_OTP}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('OTP verification response:', { status: response.status, data });

      if (response.ok) {
        // Check if this is a sign-in flow (user exists) vs signup flow (new user)
        if (data.success && data.token && !data.requirePasswordSetup) {
          // Direct sign-in - user exists and OTP verified successfully
          console.log('OTP verified - signing in existing user');
          sessionManager.setSession(data.token, data.userRole || selectedRole, phoneNumber);
          sessionStorage.removeItem('phoneNumber');
          sessionStorage.removeItem('pendingUserRole');
          
          // Close the overlay first
          onClose();
          
          // Redirect to original URL or reload
          if (returnUrl) {
            navigate(returnUrl);
          } else {
            window.location.reload(); // Reload to trigger authenticated state
          }
        } else if (data.requirePasswordSetup) {
          // Password setup required (signup or forgot password flows)
          console.log('Password setup required, showing password form');
          setOtpVerified(true);
          setAuthToken(data.token);
          setError(''); // Clear any errors
          
          // Update selected role from backend response if provided
          if (data.userRole) {
            setSelectedRole(data.userRole);
          }
        } else {
          // Fallback case
          console.log('Unexpected response format:', data);
          setError('Authentication completed but response format unexpected. Please try again.');
        }
      } else {
        console.error('OTP verification failed:', data);
        setError(data.message || 'Invalid verification code');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength (basic client-side validation)
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.AUTH_BASE_URL}${API_CONFIG.ENDPOINTS.SET_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          password: password,
          authToken: authToken,
          isSignUp: mode === 'signup',
          userRole: selectedRole
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Password set successfully, now complete the signup process
        sessionManager.setSession(authToken, selectedRole, phoneNumber);
        sessionStorage.removeItem('phoneNumber');
        sessionStorage.removeItem('pendingUserRole');
        
        // Close the overlay first
        onClose();
        
        // Redirect to original URL or reload
        if (returnUrl) {
          navigate(returnUrl);
        } else {
          window.location.reload(); // Reload to trigger authenticated state
        }
      } else {
        // Check if user already exists
        if (data.userExists) {
          // User already exists - switch to login mode
          setError('Account already exists. Switching to login...');
          setTimeout(() => {
            setMode('login');
            setOtpSent(false);
            setOtpVerified(false);
            setAuthToken('');
            setPassword('');
            setConfirmPassword('');
            setError('');
          }, 2000);
        } else {
          setError(data.message || 'Failed to set password');
        }
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Password setting error:', err);
    } finally {
      setLoading(false);
    }
  };

  // If user clicks outside the modal, close it
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
      // Don't navigate away, just close the modal and stay on homepage
    }
  };

  return (
    <div className="login-overlay" onClick={handleOverlayClick}>
      <div className="login-overlay-backdrop"></div>
      <div className="login-overlay-content">
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="overlay-header">
          <Logo size="large" showText={true} variant="dark" />
          <h2>
            {mode === 'signup' ? 'Welcome to RentalHelp AI' : 'Sign In to RentalHelp AI'}
          </h2>
        </div>

        {error && <div className="error-message">{error}</div>}

        {!otpSent ? (
          <>
            {/* Show password form for signin (unless forgot password is clicked) */}
            {mode === 'signin' && !useForgotPassword ? (
              <form onSubmit={handlePasswordSignIn} className="overlay-form">
                <div className="input-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="e.g., 4161234567"
                    required
                    disabled={loading}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? '👁️' : '🙈'}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading}>
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
                <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px', color: '#666' }}>
                  <button 
                    type="button" 
                    onClick={() => setUseForgotPassword(true)} 
                    style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Forgot Password?
                  </button>
                </p>
              </form>
            ) : (
              /* Show OTP form for signup or forgot password */
              <form onSubmit={handleRequestOtp} className="overlay-form">
                {mode === 'signup' && (
                  <div className="role-selection">
                    <p>What best describes you?</p>
                    <div className="role-buttons">
                      {roles.map(role => (
                        <button
                          key={role.key}
                          type="button"
                          className={`role-button ${selectedRole === role.key ? 'selected' : ''}`}
                          onClick={() => setSelectedRole(role.key)}
                        >
                          <div className="role-icon">{role.icon}</div>
                          <span>{role.title}</span>
                          <small>{role.description}</small>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="input-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="e.g., 4161234567"
                    required
                    disabled={loading}
                  />
                </div>
                <button type="submit" disabled={loading}>
                  {loading ? 'Sending Code...' : 'Send Verification Code'}
                </button>
                {useForgotPassword && (
                  <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px', color: '#666' }}>
                    <button 
                      type="button" 
                      onClick={() => setUseForgotPassword(false)} 
                      style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Back to Password Sign In
                    </button>
                  </p>
                )}
              </form>
            )}
            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px', color: '#666' }}>
              {mode === 'signup' ? (
                <>
                  Already have an account?{' '}
                  <button type="button" onClick={() => { setMode('signin'); setUseForgotPassword(false); }} style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}>
                    Sign In
                  </button>
                </>
              ) : (
                <>
                  Don't have an account?{' '}
                  <button type="button" onClick={() => { setMode('signup'); setUseForgotPassword(false); }} style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}>
                    Sign Up
                  </button>
                </>
              )}
            </p>
          </>
        ) : otpVerified ? (
          <form onSubmit={handlePasswordSubmit} className="overlay-form">
            <p>Create a secure password for your account</p>
            <div className="input-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-container">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <small className="password-requirements">
                Must be at least 8 characters with uppercase, lowercase, number, and special character
              </small>
            </div>
            <div className="input-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-container">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Setting Password...' : 'Complete Signup'}
            </button>
            <button 
              type="button" 
              className="back-button"
              onClick={() => setOtpVerified(false)}
            >
              Back
            </button>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="overlay-form">
            <p>A verification code has been sent to {phoneNumber}</p>
            <div className="input-group">
              <label htmlFor="otp">Verification Code</label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength="6"
                required
                disabled={loading}
              />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button 
              type="button" 
              className="back-button"
              onClick={() => setOtpSent(false)}
            >
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginOverlay; 